/**
 * 이상한 방 - 매치 세션의 체인저 배선 (도구 ↔ 전송 · 씬 · 소리 · 토스트)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(바꾸기 1분) · 4.3절(거부는 선택 불가 - 잠그지 못한 것만 토스트)
 *            `이상한방-상태모델-v0.1.md` 11.3절(변화 검증은 서버가 쥔다)
 *            로드맵 M4(`changeRejected`는 `editor.reject.*` 토스트 · 초안 → 씬) + 오케스트레이터 메모(수용 확인음 `changeAccepted`는
 *            문서 밖의 결정이다 - 예산 점만으로는 손이 "들어갔다"를 못 느껴 컨셉시트 4.3절의 귀책 인지와 같은 이유로 작은 소리 하나를 얹었다)
 *
 * `matchSession.ts`가 400줄 목표의 턱밑이라(M4 준비 몫) 바꾸기 도구의 배선은 이 파일이 받는다. 여기에도 **규칙이 없다** -
 * 도구(`input/changerTool.ts`)가 `core/verbs`로 미리 잠그고 호스트(`matchChange.ts`)가 판정하며, 이 파일은 둘 사이를
 * 잇는 문 넷이다: 도구가 보내는 이벤트(`send`) · 도구가 읽는 뷰의 조각(`snapshot`) · 도구가 그린 초안(`showDraft`) ·
 * 호스트의 답(`routeChangerEffect`).
 *
 * ## 도구는 무대가 세운다 - 세션은 그 도구를 조회할 뿐이다
 *
 * 바꾸기 진입에 세우고 수색 진입에 치우는 것은 `app/stageChanger.ts`다(수색 중에는 도구를 만들지 않는다 - 감춘 도구 금지
 * 규약의 연장). 세션은 `ChangerLink` 조회 함수 하나를 받아, 도구가 서 있을 때만 효과를 넘긴다. 도구가 없을 때 온 효과
 * (수색으로 넘어간 뒤 늦게 도착한 것)는 조용히 지나간다. M11에서는 전원이 도구를 들지만 `changerProgress`는 좌석마다
 * 나가므로(`by`), 도구로 가는 것은 `by === me` 하나뿐이다 - 남들의 진행은 진행판(`ui/zoneProgress`)이 뷰에서 읽는다.
 *
 * ## `input/`의 모양을 `game/`이 맞춘다
 *
 * 도구는 `game/`을 보지 않으므로(레이어 방향) 뷰·이벤트의 모양을 좁게 다시 적었다(`ChangerDeskView`·`ChangerToolEvent`).
 * 그 모양과 `ClientView`·`PlayerEvent`가 구조적으로 맞는지는 이 파일의 타입 대입이 컴파일에서 검사한다 - 여기서
 * 변환 함수를 쓰는 순간 그것이 두 번째 사본이다(`changerSnapshot`은 필드 이름 옮기기만이고 산술이 없다 - "지금 작업대를
 * 만질 수 있는가"도 호스트가 리듀서 게이트로 내린 `self.canChange` 그대로다).
 */

import type { RoomState } from '../core/types'
import type { VerbRejection } from '../core/verbs'
import { t } from '../i18n'
import type { MessageKey } from '../i18n/ko'
import type { ChangerTool, ChangerToolEvent, ChangerViewSnapshot } from '../input/changerTool'
import type { GameHudHandle } from '../ui/gameHud'
import type { MatchEffectBody, PlayerEvent } from './match/matchTypes'
import type { ClientView } from './match/matchView'
import type { MatchScene } from './matchSessionScene'
import type { SessionAudio } from './sessionAudio'

/* --- 타입 ------------------------------------------------------------------- */

/** 호스트의 답 중 체인저에 관한 셋. */
export type ChangerEffect = Extract<MatchEffectBody, { type: 'changeAccepted' | 'changeRejected' | 'changerProgress' }>

/** 세션이 도구에서 필요로 하는 표면. `ChangerTool`이 이것을 만족한다(구조적) - 무대가 세운 도구를 세션은 이 이름으로만 안다. */
export type ChangerLink = Pick<ChangerTool, 'onProgress' | 'onRejected' | 'onRequestDropped' | 'onView'>

/**
 * 무대가 도구를 세울 때 필요한 것 전부. 세션이 만들어 내준다(`MatchSession.changer`).
 * `send`가 `ChangerToolEvent`를 받는 것이 도구의 이벤트 모양이 `PlayerEvent`의 부분집합임을 컴파일이 확인하는 자리다.
 */
export type MatchChangerPort = Readonly<{
  /** 지금 뷰에서 도구가 읽는 조각. `welcome` 전에는 비활성이다. */
  snapshot: () => ChangerViewSnapshot
  /** 바꾸기 진입 시점의 초안(호스트가 `rooms`에 실어 준 것). 아직 없으면 원본이다. */
  draft: () => RoomState | null
  original: () => RoomState | null
  /** 도구가 그린 초안을 씬에 올린다. */
  showDraft: (room: RoomState) => void
  send: (event: ChangerToolEvent) => number
  /** 도구가 미리 잠글 수 없던 요청(자리 탭)이 verbs에 걸렸다. 호스트 거부와 같은 말로 띄운다. */
  showRejection: (reason: VerbRejection) => void
  /** 남의 구획 물건을 탭했다(M11). 거부가 아니라 아무 일도 없었음이라 사유 문구가 아닌 한 줄이다. */
  showOutOfZone: () => void
  /**
   * 잠긴 동사를 눌렀다. 문구는 띠(`ui/changerBar`)가 이미 골라서 준다 - 이유 표는 그 파일 하나에 있어야 하고
   * (`aria-label`도 같은 표를 쓴다) 여기는 자막으로 띄우기만 한다. `showNotice`도 문자열 API다.
   */
  showLocked: (text: string) => void
}>

export type MatchChangerParts = Readonly<{
  view: () => ClientView | null
  scene: MatchScene
  hud: GameHudHandle
  /** 세션의 `send`. `cseq`를 매기는 유일한 자리는 세션이고 도구의 이벤트도 그 문을 지난다. */
  send: (event: PlayerEvent) => number
}>

/* --- 뷰 조각 ------------------------------------------------------------------ */

/**
 * `ClientView` → 도구가 읽는 조각. 이름 옮기기만이다 - "바꾸기 페이즈이고 내가 다녀간 사람인가"는 호스트가 리듀서 게이트
 * (`matchDesk.canUseDesk`)로 내린 `self.canChange`고, 여기서 `phase`를 다시 보지 않는다.
 */
export function changerSnapshot(view: ClientView | null): ChangerViewSnapshot {
  if (view === null) return { active: false, desk: null, zoneRect: null, zoneId: null }
  const desk = view.desk
  return {
    active: view.self.canChange,
    // 뷰의 `desk`는 언제나 **내 작업대**다(`matchView.deskFor`) - 남의 것은 뷰에 자리가 없다.
    desk: desk === null ? null : { budget: desk.budget, used: desk.used, budgetLeft: desk.budgetLeft, touched: desk.touched ?? [] },
    // 손댈 수 있는 사각형(M11). 호스트가 배정한 내 구획 그대로다 - 도구가 좌표를 만들지 않는다.
    zoneRect: view.myZone?.rect ?? null,
    // 그 구획의 id. 띠의 이름표가 이것으로 사전을 조회한다(`ui/changerBar.ts` - `input/`은 사전을 보지 않는다).
    zoneId: view.myZone?.zoneId ?? null,
  }
}

/* --- 거부 토스트 ------------------------------------------------------------------ */

/**
 * 거부 하나 → 문구 키. 에디터와 같은 말(`editor.reject.<reason>`)이다 - 같은 거부가 두 화면에서 다르게 읽히면 안 된다.
 * 도구가 미리 잠그지 못한 로컬 거부(자리 탭)와 호스트의 `changeRejected`가 **같은 함수**를 지난다.
 *
 * 예외 하나: 구획을 받은 판(`hasZone`)의 `out-of-bounds`는 "방 밖이다"가 아니라 **"내 구획이 아니다"**로 읽는다.
 * 구획 사각형은 언제나 방 안쪽이므로 방 밖은 곧 구획 밖이고(그 역은 아니다), 구획을 받은 사람에게 실제로 걸리는 것은
 * 언제나 구획 경계다 - 벽 너머를 눌러 놓고 "방 밖"이라 들으면 자기가 무엇을 잘못했는지 알 수 없다. 규칙의 사본이 아니라
 * 같은 사유를 지금 문맥의 말로 옮기는 것이다(사유 집합은 그대로 `VerbRejection`이다 - M11 규칙 2가 새 사유를 만들지 않은 이유).
 */
export function rejectionKey(reason: VerbRejection, hasZone: boolean): MessageKey {
  if (hasZone && reason === 'out-of-bounds') return 'zone.notMine'
  const key: MessageKey = `editor.reject.${reason}`
  return key
}

function showRejection(hud: GameHudHandle, reason: VerbRejection, hasZone: boolean): void {
  hud.showNotice(t(rejectionKey(reason, hasZone)), 'warn')
}

/* --- 문 ----------------------------------------------------------------------- */

export function createMatchChangerPort(parts: MatchChangerParts): MatchChangerPort {
  /** 지금 내가 구획을 받았는가. 거부 문구가 둘 중 어느 말을 쓸지가 이 하나에서 갈린다(`rejectionKey`). */
  const hasZone = (): boolean => parts.view()?.myZone != null

  return {
    snapshot: () => changerSnapshot(parts.view()),
    draft: () => {
      const rooms = parts.scene.rooms()
      return rooms.mutated ?? rooms.original
    },
    original: () => parts.scene.rooms().original,
    showDraft: (room) => parts.scene.showDraft(room),
    send: (event) => parts.send(event),
    showRejection: (reason) => showRejection(parts.hud, reason, hasZone()),
    showOutOfZone: () => parts.hud.showNotice(t('zone.notMine'), 'warn'),
    showLocked: (text) => parts.hud.showNotice(text, 'warn'),
  }
}

/* --- 호스트의 답 ---------------------------------------------------------------- */

/**
 * 체인저 효과 하나 → 도구·소리·토스트. 수용은 작은 소리 하나(`changeAccepted`)고 예산 점은 뒤따르는 `changerProgress`가
 * 도구를 통해 채운다. 거부는 에디터와 같은 말(`editor.reject.<reason>`)로 띄운다 - 같은 거부가 두 화면에서 다르게 읽히면
 * 안 된다. 도구가 없으면(이미 수색이다 · 바꾸기가 없는 라운드다) 소리도 토스트도 없다 - 남의 작업에 내 화면이 울릴 이유가 없다.
 */
export function routeChangerEffect(
  effect: ChangerEffect,
  link: ChangerLink | null,
  audio: SessionAudio,
  hud: GameHudHandle,
  hasZone: boolean,
): void {
  if (link === null) return
  switch (effect.type) {
    case 'changeAccepted':
      audio.play('changeAccepted')
      return
    case 'changeRejected':
      showRejection(hud, effect.reason, hasZone)
      link.onRejected(effect.reason)
      return
    case 'changerProgress':
      link.onProgress()
      return
  }
}
